"use client";

import React, { useState, useMemo } from "react";
import { useTranslation, Trans } from "react-i18next";
import {
    Modal,
    Input,
    Select,
    Button,
    Space,
    Tag,
    Rate,
    Typography,
    Checkbox,
    Alert,
} from "antd";
import { SearchOutlined, StarFilled } from "@ant-design/icons";
import { CodeCreate, CodeType, ProblemRead } from "@openapi";
import { getDefaultExampleForCodeType } from "@/config/defaultExamples";
import { readStorage, writeStorage } from "@/lib/storage";
import ChooseProblemModal from "../ChooseProblemModal";
import {
    CODE_TYPE_CONFIG,
    codeTypeToProblemType,
    colorForProblemType,
    complexityToStars,
    problemTypeDisplayName,
} from "@/utils/api";

const { Text } = Typography;

// LocalStorage key for include example code preference
const INCLUDE_EXAMPLE_CODE_KEY = "addCodeModal.includeExampleCode";

interface AddCodeModalProps {
    isVisible: boolean;
    isLocal: boolean;
    onClose: () => void;
    onSave: (newCode: CodeCreate) => void;
}

const AddCodeModal: React.FC<AddCodeModalProps> = ({
    isVisible,
    isLocal,
    onClose,
    onSave,
}) => {
    const { t } = useTranslation();
    // Form state
    const [name, setName] = useState("");
    const [codeType, setCodeType] = useState<CodeType>(CodeType.Logo);
    const [selectedProblem, setSelectedProblem] = useState<ProblemRead | null>(
        null,
    );

    // Include example code checkbox state (with localStorage persistence)
    const [includeExampleCode, setIncludeExampleCode] = useState<boolean>(
        () => {
            const saved = readStorage(INCLUDE_EXAMPLE_CODE_KEY);
            return saved !== null ? saved === "true" : true; // Default to true
        },
    );

    // Save includeExampleCode preference to localStorage
    const handleIncludeExampleCodeChange = (checked: boolean) => {
        setIncludeExampleCode(checked);
        writeStorage(INCLUDE_EXAMPLE_CODE_KEY, String(checked));
    };

    // Problem selector modal state
    const [isProblemModalVisible, setIsProblemModalVisible] = useState(false);

    // Check if selected problem language matches the code type
    const problemLanguageMismatch = useMemo(() => {
        if (!selectedProblem) return false;
        const expectedType = codeTypeToProblemType(codeType);
        if (!expectedType) return false;
        return selectedProblem.problem_type !== expectedType;
    }, [selectedProblem, codeType]);

    // Handle problem selection
    const handleSelectProblem = (problem: ProblemRead) => {
        setSelectedProblem(problem);
        // Auto-fill name if empty
        if (!name) {
            setName(problem.name);
        }
        setIsProblemModalVisible(false);
    };

    // Handle clear problem selection
    const handleClearProblem = () => {
        setSelectedProblem(null);
    };

    // Handle save
    const handleOk = () => {
        // Get example code if checkbox is checked
        const example = includeExampleCode
            ? getDefaultExampleForCodeType(codeType)
            : null;

        // Determine blockly_workspace value
        let blockly_workspace: string | null = null;
        if (codeType.endsWith("_blockly")) {
            // Use example workspace if available and checkbox checked, otherwise use "null"
            blockly_workspace = example?.blocklyWorkspace || "null";
        }

        const newCode: CodeCreate = {
            name: name || selectedProblem?.name || t("addCodeModal.untitled"),
            type: codeType,
            code: example?.code || "",
            user_id: "",
            blockly_workspace,
            problem_id: selectedProblem?.id || null,
        };
        onSave(newCode);
        // Reset state
        setName("");
        setCodeType(CodeType.Logo);
        setSelectedProblem(null);
        onClose();
    };

    // Handle close
    const handleClose = () => {
        setName("");
        setCodeType(CodeType.Logo);
        setSelectedProblem(null);
        onClose();
    };

    // Render code type select option
    const renderCodeTypeOption = (type: CodeType) => {
        const config = CODE_TYPE_CONFIG[type];
        return (
            <Space>
                <span style={{ color: config.color }}>{config.icon}</span>
                <span>{config.label}</span>
            </Space>
        );
    };

    return (
        <>
            {/* Main Add Code Modal */}
            <Modal
                title={t("addCodeModal.title")}
                open={isVisible}
                onOk={handleOk}
                onCancel={handleClose}
                okText={t("addCodeModal.create")}
                cancelText={t("addCodeModal.cancel")}
                okButtonProps={{ disabled: !name.trim() && !selectedProblem }}
                width={500}
            >
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                    }}
                >
                    {/* Code Type Selection */}
                    <div>
                        <Text
                            strong
                            style={{ display: "block", marginBottom: 8 }}
                        >
                            {t("addCodeModal.codeType")}
                        </Text>
                        <Select
                            value={codeType}
                            onChange={setCodeType}
                            style={{ width: "100%" }}
                            optionLabelProp="label"
                            showScrollBar
                        >
                            {Object.values(CodeType).map((type) => (
                                <Select.Option
                                    key={type}
                                    value={type}
                                    label={CODE_TYPE_CONFIG[type].label}
                                >
                                    {renderCodeTypeOption(type)}
                                </Select.Option>
                            ))}
                        </Select>
                    </div>

                    {/* Problem Selection (not for local) */}
                    {!isLocal && (
                        <div>
                            <Text
                                strong
                                style={{ display: "block", marginBottom: 8 }}
                            >
                                {t("addCodeModal.problemOptional")}
                            </Text>
                            {selectedProblem ? (
                                <div
                                    style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 8,
                                        padding: "8px 12px",
                                        background: "#f5f5f5",
                                        borderRadius: 6,
                                    }}
                                >
                                    <Tag
                                        color={colorForProblemType(
                                            selectedProblem.problem_type,
                                        )}
                                    >
                                        {problemTypeDisplayName(
                                            selectedProblem.problem_type,
                                        )}
                                    </Tag>
                                    <Text strong style={{ flex: 1 }} ellipsis>
                                        {selectedProblem.name}
                                    </Text>
                                    {selectedProblem.complexity != null && (
                                        <Rate
                                            disabled
                                            value={complexityToStars(
                                                selectedProblem.complexity,
                                            )}
                                            character={
                                                <StarFilled
                                                    style={{ fontSize: 10 }}
                                                />
                                            }
                                        />
                                    )}
                                    <Button
                                        type="link"
                                        size="small"
                                        danger
                                        onClick={handleClearProblem}
                                    >
                                        {t("addCodeModal.clear")}
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    onClick={() =>
                                        setIsProblemModalVisible(true)
                                    }
                                    icon={<SearchOutlined />}
                                    style={{ width: "100%" }}
                                >
                                    {t("addCodeModal.chooseProblem")}
                                </Button>
                            )}
                            {/* Warning for language mismatch */}
                            {problemLanguageMismatch && (
                                <Alert
                                    type="warning"
                                    showIcon
                                    style={{ marginTop: 8 }}
                                    title={
                                        <Trans
                                            i18nKey="addCodeModal.languageMismatch"
                                            values={{
                                                problemType:
                                                    problemTypeDisplayName(
                                                        selectedProblem?.problem_type ??
                                                            null,
                                                    ),
                                                codeType:
                                                    CODE_TYPE_CONFIG[codeType]
                                                        .label,
                                            }}
                                            components={{
                                                1: <strong />,
                                                3: <strong />,
                                            }}
                                        />
                                    }
                                />
                            )}
                        </div>
                    )}

                    {/* Name Input */}
                    <div>
                        <Text
                            strong
                            style={{ display: "block", marginBottom: 8 }}
                        >
                            {t("addCodeModal.name")}
                        </Text>
                        <Input
                            placeholder={t("addCodeModal.namePlaceholder")}
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            onPressEnter={handleOk}
                        />
                        {selectedProblem && !name && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {t("addCodeModal.useProblemNameHint", {
                                    name: selectedProblem.name,
                                })}
                            </Text>
                        )}
                    </div>

                    {/* Include Example Code Checkbox */}
                    <Checkbox
                        checked={includeExampleCode}
                        onChange={(e) =>
                            handleIncludeExampleCodeChange(e.target.checked)
                        }
                    >
                        {t("addCodeModal.includeExampleCode")}
                    </Checkbox>
                </div>
            </Modal>

            {/* Problem Selection Modal */}
            <ChooseProblemModal
                onClose={() => setIsProblemModalVisible(false)}
                isVisible={isProblemModalVisible}
                onSelect={handleSelectProblem}
                codeType={codeType}
            />
        </>
    );
};

export default AddCodeModal;
